import axios from "../axios";

const normalizeNote = (note) => {
  if (!note) return null;
  const timePackageItem =
    note.timePackageItem ||
    note.time_package_item ||
    note["time_package_item"] ||
    note["timePackage_item"] ||
    null;

  const normalized = {
    id: note.id,
    content: note.content,
    type: note.type,
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
    timePackageItemId:
      note.timePackageItemId ||
      note.timePackageItemID ||
      note.time_package_item_id ||
      (timePackageItem ? timePackageItem.id : null),
    timePackageItem: timePackageItem
      ? {
          id: timePackageItem.id,
          name: timePackageItem.name,
          forBeginners: timePackageItem.forBeginners,
        }
      : null,
  };

  return normalized;
};

const notes = {
  namespaced: true,
  state: () => ({
    notesByVideoId: {},
    allNotes: [],
    availableVideos: [],
  }),
  mutations: {
    SET_NOTE_FOR_VIDEO(state, note) {
      if (note && note.timePackageItemId) {
        state.notesByVideoId = {
          ...state.notesByVideoId,
          [note.timePackageItemId]: note,
        };
      }
    },
    REMOVE_NOTE_FOR_VIDEO(state, timePackageItemId) {
      const updated = { ...state.notesByVideoId };
      delete updated[timePackageItemId];
      state.notesByVideoId = updated;
    },
    SET_ALL_NOTES(state, notes) {
      state.allNotes = notes || [];
    },
    SET_AVAILABLE_VIDEOS(state, videos) {
      state.availableVideos = videos || [];
    },
  },
  actions: {
    async fetchNoteForVideo({ commit }, timePackageItemId) {
      if (!timePackageItemId) return null;
      try {
        const response = await axios.get(
          `/notes/timePackageItems/${timePackageItemId}`
        );
        const note =
          response.data && response.data.length
            ? normalizeNote(response.data[0])
            : null;
        if (note) {
          commit("SET_NOTE_FOR_VIDEO", note);
        } else {
          commit("REMOVE_NOTE_FOR_VIDEO", timePackageItemId);
        }
        return note;
      } catch (error) {
        return null;
      }
    },

    async saveNote(
      { state, commit, dispatch },
      { timePackageItemId, content }
    ) {
      if (!timePackageItemId) return null;
      const existingNote = state.notesByVideoId[timePackageItemId];
      try {
        let response;
        if (existingNote) {
          response = await axios.put(`/notes/${existingNote.id}`, { content });
        } else {
          response = await axios.post(
            `/notes/timePackageItems/${timePackageItemId}`,
            { content }
          );
        }
        const normalized = normalizeNote(response.data);
        if (normalized) {
          commit("SET_NOTE_FOR_VIDEO", normalized);
        }
        dispatch(
          "snackbar/setSnackbar",
          {
            color: "success",
            icon: "check",
            message: "Notatka zapisana",
          },
          { root: true }
        );
        dispatch("snackbar/toggleSnackbar", true, { root: true });
        return normalized;
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Nie udało się zapisać notatki. Spróbuj ponownie.";
        dispatch(
          "snackbar/setSnackbar",
          {
            color: "red",
            icon: "exclamation-triangle",
            message,
          },
          { root: true }
        );
        dispatch("snackbar/toggleSnackbar", true, { root: true });
        return null;
      }
    },

    async deleteNote({ commit, dispatch }, { noteId, timePackageItemId }) {
      if (!noteId) return;
      try {
        await axios.delete(`/notes/${noteId}`);
        commit("REMOVE_NOTE_FOR_VIDEO", timePackageItemId);
        dispatch(
          "snackbar/setSnackbar",
          {
            color: "success",
            icon: "check",
            message: "Notatka usunięta",
          },
          { root: true }
        );
        dispatch("snackbar/toggleSnackbar", true, { root: true });
      } catch (error) {
        dispatch(
          "snackbar/setSnackbar",
          {
            color: "red",
            icon: "exclamation-triangle",
            message: "Nie udało się usunąć notatki",
          },
          { root: true }
        );
        dispatch("snackbar/toggleSnackbar", true, { root: true });
      }
    },

    async exportNoteToPdf({ dispatch }, noteId) {
      if (!noteId) return;
      try {
        const response = await axios.get(`/notes/${noteId}/export-pdf`, {
          responseType: "blob",
        });

        // Create a blob from the PDF data
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        // Create a temporary link and trigger download
        const link = document.createElement("a");
        link.href = url;
        link.download = `notatka-${noteId}.pdf`;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        dispatch(
          "snackbar/setSnackbar",
          {
            color: "success",
            icon: "check",
            message: "PDF został pobrany",
          },
          { root: true }
        );
        dispatch("snackbar/toggleSnackbar", true, { root: true });
        return true;
      } catch (error) {
        dispatch(
          "snackbar/setSnackbar",
          {
            color: "red",
            icon: "exclamation-triangle",
            message: "Nie udało się wyeksportować PDF",
          },
          { root: true }
        );
        dispatch("snackbar/toggleSnackbar", true, { root: true });
        return false;
      }
    },

    async updateNote({ commit, dispatch }, { noteId, content }) {
      if (!noteId) return null;
      try {
        const response = await axios.put(`/notes/${noteId}`, { content });
        const normalized = normalizeNote(response.data);
        if (normalized) {
          commit("SET_NOTE_FOR_VIDEO", normalized);
        }
        dispatch(
          "snackbar/setSnackbar",
          {
            color: "success",
            icon: "check",
            message: "Notatka zaktualizowana",
          },
          { root: true }
        );
        dispatch("snackbar/toggleSnackbar", true, { root: true });
        return normalized;
      } catch (error) {
        dispatch(
          "snackbar/setSnackbar",
          {
            color: "red",
            icon: "exclamation-triangle",
            message: "Nie udało się zaktualizować notatki",
          },
          { root: true }
        );
        dispatch("snackbar/toggleSnackbar", true, { root: true });
        return null;
      }
    },

    async fetchAllNotes({ commit }, params = {}) {
      try {
        const response = await axios.get("/notes", {
          params: params.type ? { type: params.type } : {},
        });
        const normalized = Array.isArray(response.data)
          ? response.data.map((note) => normalizeNote(note))
          : [];
        commit("SET_ALL_NOTES", normalized);
        return normalized;
      } catch (error) {
        commit("SET_ALL_NOTES", []);
        return [];
      }
    },

    async fetchAvailableVideos({ commit }) {
      try {
        const [general, beginners] = await Promise.all([
          axios.get("/timePackageItems", {
            params: { includeItemsWithSubcategory: true },
          }),
          axios.get("/timePackageItems", {
            params: { includeItemsWithSubcategory: true, forBeginners: true },
          }),
        ]);
        const merged = [...(general.data || []), ...(beginners.data || [])];
        const uniqueById = [];
        const map = {};
        merged.forEach((item) => {
          if (!item || !item.id || map[item.id]) return;
          map[item.id] = true;
          uniqueById.push({
            id: item.id,
            name: item.name,
            forBeginners: item.forBeginners,
          });
        });
        uniqueById.sort((a, b) => a.name.localeCompare(b.name));
        commit("SET_AVAILABLE_VIDEOS", uniqueById);
        return uniqueById;
      } catch (error) {
        commit("SET_AVAILABLE_VIDEOS", []);
        return [];
      }
    },
  },
  getters: {
    getNoteByVideoId: (state) => (timePackageItemId) =>
      state.notesByVideoId[timePackageItemId],
    getAllNotes: (state) => state.allNotes,
    getAvailableVideos: (state) => state.availableVideos,
  },
};

export default notes;
